import { useCallback, useEffect, useState } from 'react';
import {
  buildPaperMap,
  loadStructuredSnapshot,
  validate,
  type PaperMap,
  type ValidationDiagnostic,
} from '@/lib/writer/structured';

/**
 * Loads the structured snapshot (sections / preamble / bib) for a project and
 * derives the paper map + pre-compile diagnostics client-side. Cheap enough
 * to re-run whenever the agent applies an edit — the WriterProject page just
 * bumps `refreshTick` after each accepted artifact or tool result.
 */
export function useWriterPaperMap(
  projectId: string | undefined,
  refreshTick = 0,
) {
  const [paperMap, setPaperMap] = useState<PaperMap | null>(null);
  const [diagnostics, setDiagnostics] = useState<ValidationDiagnostic[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const snap = await loadStructuredSnapshot(projectId);
      setPaperMap(buildPaperMap(snap));
      setDiagnostics(
        validate({ sections: snap.sections, bibEntries: snap.bibEntries }),
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload, refreshTick]);

  return { paperMap, diagnostics, loading, reload };
}
